import variables from '../../../stylesheets/variables.module.scss';

// they should sum up to 100
export const sectionContentScoreWeight = {
    HOTEL_SERVICES: 50,
    ROOM_SERVICES: 30,
    IMAGES: 20,
};

export type ContentStatusItemScore = {
    value: number;
    maxValue: number;
};

export type SectionContentScore = Record<
    keyof typeof sectionContentScoreWeight,
    number
>;

export const calculateContentStatusPercentage = (
    items: ContentStatusItemScore[]
) => {
    const applicableItems = items.filter(item => item.maxValue > 0);
    if (applicableItems.length === 0) {
        return 0;
    }

    return (
        applicableItems.reduce(
            (total, item) => total + (item.value / item.maxValue) * 100,
            0
        ) / applicableItems.length
    );
};

export enum ZitoneoQualityLevel {
    LOW = 'Low',
    MEDIUM = 'Medium',
    HIGH = 'High',
}

export const zitoneoQualityLevelColor = {
    [ZitoneoQualityLevel.HIGH]: variables.primary400,
    [ZitoneoQualityLevel.MEDIUM]: variables.accentColorOrange600,
    [ZitoneoQualityLevel.LOW]: variables.disabledButton,
};

export const zitoneoPresentationQualityLevelColor = {
    [ZitoneoQualityLevel.HIGH]: variables.primary400,
    [ZitoneoQualityLevel.MEDIUM]: '#ef9f27',
    [ZitoneoQualityLevel.LOW]: 'var(--error-700)',
};
export const getZitoneoPresentationQualityLevel = (
    percentage: number
): { level: ZitoneoQualityLevel; color: string } => {
    if (percentage >= 80) {
        return {
            level: ZitoneoQualityLevel.HIGH,
            color: zitoneoPresentationQualityLevelColor[
                ZitoneoQualityLevel.HIGH
            ],
        };
    }
    if (percentage >= 50) {
        return {
            level: ZitoneoQualityLevel.MEDIUM,
            color: zitoneoPresentationQualityLevelColor[
                ZitoneoQualityLevel.MEDIUM
            ],
        };
    }
    return {
        level: ZitoneoQualityLevel.LOW,
        color: zitoneoPresentationQualityLevelColor[ZitoneoQualityLevel.LOW],
    };
};

export const calculateGlobalContentScore = (
    sectionScores: SectionContentScore
) => {
    const percentage =
        (sectionScores.HOTEL_SERVICES *
            sectionContentScoreWeight.HOTEL_SERVICES +
            sectionScores.ROOM_SERVICES *
                sectionContentScoreWeight.ROOM_SERVICES +
            sectionScores.IMAGES * sectionContentScoreWeight.IMAGES) /
        100;
    const { level, color } = getZitoneoPresentationQualityLevel(percentage);

    return { percentage, level, color };
};

/** Returns the translation key for the global content quality subtext. */
export const getSubtextForGlobalContentScore = (
    level: ZitoneoQualityLevel
): string => {
    switch (level) {
        case ZitoneoQualityLevel.HIGH:
            return 'excellent_content_coverage';
        case ZitoneoQualityLevel.MEDIUM:
            return 'good_content_coverage_room_for_improvement';
        case ZitoneoQualityLevel.LOW:
            return 'poor_content_coverage_needs_attention';
        default:
            return '';
    }
};
